<template>
    <div class="special-container">
        <Title v-if="drawDate && drawDate.drawDate" :title="renderDateText(drawDate.drawDate)" :titleOption="`- ${renderHour(drawDate.drawDate)}`" />
        <div class="special-content" v-for="(m, index) in bettableMarkets" :key="index">
            <ListNumber :numbers="numbers" :isShowLuckdip="false" :title="m.description" :maxPick="1"
                :outcomes="m.outcomes" v-if="m.config.layout.type == 'balls'" />
            <MarketColor v-if="m.config.layout.type == 'colors'" :outcomes="m.outcomes" :title="m.description"
                @addColor="handleAddColor" />
            <MarketSelect v-if="m.config.layout.type == 'select'" :outcomes="m.outcomes" :title="m.description"
                @addSelect="handleAddSelect" />

            <MarketTotalNumber v-if="m.config.layout.type == 'total-balls'" :outcomes="m.outcomes"
                :title="m.description" @addTotalBall="addTotalBall" />
        </div>
    </div>
</template>
<script>
import utilsMixin from '@/mixins/function-utils';
import betslipApiMixin from '@/mixins/betslip-api-mixin';
import Title from "@/components/common/title/index.vue";
import ListNumber from "./components/list-number.vue";
import MarketColor from "./components/market-color.vue";
import MarketSelect from "./components/market-select.vue";
import MarketTotalNumber from "./components/market-total-number.vue";
import EventBus from '@/utils/EventBus';
import { resolveStake, isOutcomeBettable } from "@/utils/betslip-transform";
import { getStateStore } from "@/utils/utils";
import { notify } from "@/utils/notify";
import languageMixins from "@/mixins/language-mixins";

export default {
    mixins: [languageMixins, utilsMixin, betslipApiMixin],
    components: { Title, ListNumber, MarketColor, MarketSelect, MarketTotalNumber },
    props: {
        marketSlug: {
            type: String,
        }
    },
    computed: {
        accountSetting() {
            // Live store reference. NOT getStateStore(): that deep-clones the entire
            // state via JSON round-trip, so the computed would depend on all of it and
            // return a new object on every unrelated change.
            return this.$store.state.accountSetting;
        },
        bettableMarkets() {
            return (this.rawMarkets || [])
                .map((m) => ({
                    ...m,
                    outcomes: (m.outcomes || []).filter((o) => isOutcomeBettable(o, this.accountSetting)),
                }))
                // Drop whole market blocks that end up with nothing left to show.
                .filter((m) => m.outcomes.length);
        },
    },
    data() {
        return {
            // Unfiltered source; bettability is derived so a late accountSetting re-evaluates.
            rawMarkets: [],
            numbers: [],
            numbersSelected: [],
            drawDate: {},
        }
    },
    watch: {
        "$store.state.gameMarket"() {
            this.handleGetDataSpecial();
        },
        "$store.state.nextDraws"() {
            this.handleGetDataSpecial();
        },
    },
    created() {
        this.handleGetDataSpecial()
        this.eventBusCallback = (numbers) => this.addNumber(numbers);
        EventBus.on('add-number', this.eventBusCallback);
    },
    unmounted() {
        EventBus.off('add-number', this.eventBusCallback)
    },
    methods: {
        handleGetDataSpecial() {
            const gameSlug = this.$route.params.gameSlug;
            const draw = this.$store.getters.drawDate(gameSlug);
            if (draw) this.drawDate = draw;
            const game = (this.$store.state.gameMarket || []).find(g => g.slug == gameSlug);
            if (!game) return;

            const groups = game.groups || [];
            this.numbers = game.balls;
            this.rawMarkets = groups.find(m => m.slug == 'special-markets')?.markets || [];
        },
        buildSpecialPayload(outcome, numbers) {
            // Clamp the submitted stake to the outcome/account limits (raw bet_default
            // can exceed payout_max); fall back to account-level limits if the outcome
            // carries none, so the clamp can't silently no-op.
            const { wager, valid } = resolveStake(outcome.bet_default, outcome, getStateStore().accountSetting);
            if (!valid) {
                notify({ text: this.getTranslatedText("This selection cannot be bet at the current stake limits."), type: "error" });
                return null;
            }
            return {
                bets: [{
                    wager,
                    event_id: this.drawDate.eventId,
                    market_options_id: outcome.id,
                    combination: false,
                    numbers,
                }],
            };
        },
        handleAddColor(outcome) {
            const payload = this.buildSpecialPayload(outcome, [[], []]);
            if (!payload) return;
            this.submitToBetslip(payload, { outcome });
        },
        handleAddSelect(outcome) {
            const payload = this.buildSpecialPayload(outcome, [[], []]);
            if (!payload) return;
            this.submitToBetslip(payload, { outcome });
        },
        async addNumber(data) {
            const outcome = data.outcomes[0];
            const payload = this.buildSpecialPayload(outcome, [data.numbersSelected.map(x => x.value)]);
            if (!payload) return;
            const success = await this.submitToBetslip(payload, { outcome });
            if (success) {
                data.onSuccess?.();
            }
        },
        addTotalBall(outcome) {
            const payload = this.buildSpecialPayload(outcome, [[], []]);
            if (!payload) return;
            this.submitToBetslip(payload, { outcome });
        },
    }
}
</script>

<style lang="scss" scoped>
@import "@/public/scss/components/coupons/special.scss";
</style>
